import {Inject, Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {HTTP_STATUS_HANDLING, HttpStatusHandling, MesHttpStatusHandlerService} from './mes-http-status-handler.service';
import {Observable, of, throwError} from 'rxjs';
import {AppConfig} from './app.config';
import {catchError, tap} from 'rxjs/operators';
import {ApiMessage, ApiResponse} from "./api-response/api-response.model";
import {DateTime} from "luxon";

export function getAuthTokenKey(name: string) {
  return name + ':authorizationData';
}

@Injectable({
  providedIn: 'root'
})
export class MesHttpInterceptor implements HttpInterceptor {
  private readonly appVersionHeaderName = 'X-App-Version';
  private readonly requestTimeHeaderName = 'X-Request-Time';
  private readonly responseDurationHeaderName = 'X-Response-Time';
  private readonly logTimingHeaderName = "X-Log-Timing";
  private appVersion = '';
  requests: string[] = [];

  constructor(@Inject('config') private appConfig: AppConfig, private httpStatusHandlerService: MesHttpStatusHandlerService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!req.headers.has('Content-Type')) {
      req = req.clone({headers: req.headers.set('Content-Type', 'application/json')});
    }
    let handleResponse = req.headers.get('handleResponse');
    if (handleResponse == null) {
      handleResponse = true.toString();
    }

    this.handleRequestLogging('Start', req.url);

    let httpStatusHandling = req.context.get(HTTP_STATUS_HANDLING);

    if (httpStatusHandling.clear) {
      this.httpStatusHandlerService.clear.next(true);
    }

    let token = null;
    try {
      const authData = JSON.parse(this.appConfig.Storage.getItem(getAuthTokenKey(this.appConfig.name)));
      if (authData) {
        token = authData.Token;
      }
    } catch (e) {
      console.error(e);
    }

    let setHeaders: any = {
      'Accept': 'application/json'
    };
    setHeaders[this.appVersionHeaderName] = this.appVersion;

    setHeaders[this.requestTimeHeaderName] = new Date().toISOString();
    if (token) {
      setHeaders['Authorization'] = `Bearer ${token}`;
    }

    for (let header of this.appConfig.Headers) {
      setHeaders[header.key] = header.value;
    }

    const httpReq = req.clone({
      setHeaders: setHeaders
    });

    // send the newly created request
    this.requests = [...this.requests, httpReq.urlWithParams];
    this.httpStatusHandlerService.urls.next(this.requests);
    return next.handle(httpReq).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          this.requests = [...this.requests.filter(value => value !== httpReq.urlWithParams)];
          this.httpStatusHandlerService.urls.next(this.requests);
          this.setAppVersion(event);
          this.handleRequestLogging('End', event.url, event.headers, event.status)
          if (handleResponse === true.toString()) {
            if(event.body instanceof Blob){
              let xErrorMessage = event.headers.get('x-error-message');
              if(xErrorMessage){
                this.httpStatusHandlerService.httpError.next(new HttpErrorResponse({error: new ApiResponse({Errors: [new ApiMessage({Messages: [xErrorMessage]})], status: 500})}));
              }
            }
            this.httpStatusHandlerService.httpResponse.next(event);
          }
        }
      })
      , catchError((error: HttpErrorResponse) => {
        if (error instanceof HttpErrorResponse && error.error instanceof Blob && error.error.type === "application/json") {
          // https://github.com/angular/angular/issues/19888
          // When request of type Blob, the error is also in Blob instead of object of the json data

          let reader = new FileReader();
          reader.onload = (e: Event) => {
            try {
              const errmsg = JSON.parse((<any>e.target).result);
              error = new HttpErrorResponse({
                error: errmsg,
                headers: error.headers,
                status: error.status,
                statusText: error.statusText,
                url: error.url
              });
              return this.handleError(error, httpStatusHandling);
            } catch (e) {
              throwError(e)
            }
          };
          reader.onerror = (e) => {
            throwError(error);
          };
          reader.readAsText(error.error);
        } else {
          return this.handleError(error, httpStatusHandling);
        }
      })) as any;
  }

  handleError(error: HttpErrorResponse, handling: HttpStatusHandling = null) {
    if (error.error && typeof (error.error) === 'object') {
      error.error.Timestamp = new Date();
    }

    this.handleRequestLogging('Error', error.url, error.headers, error.status);

    handling = handling ?? new HttpStatusHandling();

    const isMissingErrorMessages = !error.error?.Errors?.length
      && !error.error?.Warnings?.length
      && !error.error?.Informations?.length
      && error.ok === false;

    let shouldHandleGeneralError = handling.GeneralError;

    if (error.status === 409) {
      if (handling.status409) {
        this.httpStatusHandlerService.http409.next(error.error);
      } else {
        shouldHandleGeneralError = false;
      }
    }

    if (error.status === 404) {
      if (handling.status404) {
        this.httpStatusHandlerService.http404.next(error.error || true);
      } else {
        shouldHandleGeneralError = false;
      }
    }

    if (error.status === 403) {
      shouldHandleGeneralError = false;
      if (handling.status403) {
        this.httpStatusHandlerService.http403.next(error.error || true);
      } else {
        shouldHandleGeneralError = false;
      }
    }

    if (error.status === 401) {
      if (handling.status401) {
        this.httpStatusHandlerService.http401.next(error.error || true);
      } else {
        shouldHandleGeneralError = false;
      }
    }

    if (error.status === 503) {
      if (handling.status503) {
        this.httpStatusHandlerService.http503.next(error.error || true);
      } else {
        shouldHandleGeneralError = false;
      }
    }

    if (error.status === 500) {
      if (handling.status500) {
        this.httpStatusHandlerService.http500.next(error.error || true);
      } else {
        shouldHandleGeneralError = false;
      }
    }

    if (error.status === 418) {
      if (handling.status418) {
        this.httpStatusHandlerService.http418.next(error.error || true);
      } else {
        shouldHandleGeneralError = false;
      }
    }

    if (error.status === 412) {
      if (handling.status412) {
        this.httpStatusHandlerService.http412.next(error.error);
      } else {
        shouldHandleGeneralError = false;
      }
    }

    if (shouldHandleGeneralError) {
      let xErrorMessage = error.headers.get('x-error-message');
      this.httpStatusHandlerService.httpError.next(isMissingErrorMessages
        ? new HttpErrorResponse({error: new ApiResponse({Errors: [new ApiMessage({Messages: [error.message, xErrorMessage]})], status: error.status})})
        : error);
      // return the error to the method that called it
      return throwError(() => error);
    }else{
      return of(error);
    }
  }

  setAppVersion(response: HttpResponse<any>): void {
    if (!this.appVersion && response.headers.has(this.appVersionHeaderName)) {
      this.appVersion = response.headers.get(this.appVersionHeaderName);
    }
  }

  private handleRequestLogging(log: string, url: string, headers: HttpHeaders = null, status: number = null) {
    if (!headers?.has(this.logTimingHeaderName)) return;

    let requestMessage = `[ime] Request ${log}: '${url}'`;
    if (headers?.has(this.requestTimeHeaderName)) {
      let s = headers.get(this.requestTimeHeaderName);
      let date = DateTime.fromISO(s);
      let now = DateTime.now();
      let number = now.diff(date);
      requestMessage += ', Request time: ' + number.toISOTime();
      if (headers?.has(this.responseDurationHeaderName)) {
        let s = headers.get(this.responseDurationHeaderName);
        requestMessage += ', Response time: ' + s;
      }
    }

    console.log(requestMessage);
  }
}
